// Libs
import { useEffect, useRef } from "react";

interface Props {
  htmlContent: string;
  className?: string;
  style?: React.CSSProperties;
}

export default function SafeEmailPreview({
  htmlContent,
  className,
  style,
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (iframeRef.current && htmlContent) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;

      if (doc) {
        // Create a complete HTML document with proper CSS reset
        const fullHtml = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                /* Reset all margins and padding */
                * {
                  box-sizing: border-box;
                }
                
                body {
                  margin: 0;
                  padding: 16px;
                  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                  font-size: 14px;
                  line-height: 1.5;
                  color: #000;
                  background: #fff;
                  overflow-x: hidden;
                  word-wrap: break-word;
                }
                
                /* Ensure images are responsive */
                img {
                  max-width: 100%;
                  height: auto;
                }
                
                /* Table styling for email layouts */
                table {
                  border-collapse: collapse;
                  width: 100%;
                  max-width: 100%;
                }
                
                td, th {
                  padding: 8px;
                  text-align: left;
                }
                
                /* Basic typography */
                h1, h2, h3, h4, h5, h6 {
                  margin: 1em 0 0.5em 0;
                  font-weight: bold;
                }
                
                p {
                  margin: 0 0 1em 0;
                }
                
                a {
                  color: #0066cc;
                  text-decoration: underline;
                }
                
                /* Prevent horizontal overflow */
                * {
                  max-width: 100%;
                }
              </style>
            </head>
            <body>
              ${htmlContent}
            </body>
          </html>
        `;

        doc.open();
        doc.write(fullHtml);
        doc.close();

        // Auto-resize iframe to fit content
        const resizeIframe = () => {
          try {
            const body = doc.body;
            const html = doc.documentElement;

            if (body && html) {
              const height = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight,
              );

              // Set a reasonable max height and min height
              const finalHeight = Math.min(Math.max(height, 100), 600);
              iframe.style.height = `${finalHeight}px`;
            }
          } catch (error) {
            // Fallback height if measuring fails
            iframe.style.height = "300px";
          }
        };

        // Resize after content loads
        setTimeout(resizeIframe, 100);

        // Also resize on window resize
        const handleResize = () => setTimeout(resizeIframe, 100);
        window.addEventListener("resize", handleResize);

        return () => {
          window.removeEventListener("resize", handleResize);
        };
      }
    }
  }, [htmlContent]);

  return (
    <iframe
      ref={iframeRef}
      className={className}
      style={{
        width: "100%",
        border: "none",
        background: "white",
        ...style,
      }}
      title="Email Preview"
      sandbox="allow-same-origin"
      // Security: prevent scripts from running
      srcDoc=""
    />
  );
}
